import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import { createSku, CreateSkuPayload, loadMaterialOptions } from '../../api/material';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { I18n } from '../../utils/i18n';
import { toast } from '../../utils/toast';
import { SkuPropSelect } from '../../components/SkuProp';
import { SkuSpecInput } from '../../components/SkuSpec';
import { EMPTY_SKU_SPEC, useSkuSchemas, validateSkuSpec } from '../../utils/skuSpec';
import { SKU_STATUS_OPTS } from './selectOptions';

interface Props {
    open: boolean;
    onClose: () => void;
    /** 直接入库成功后回调 (走审批流时数据未变, 不触发) */
    onCreated?: () => void;
    /** 材质编码预填 (SKU 库带 ?xcode= 筛选时) */
    presetXcode?: string;
}

// 新增单个 SKU. 字段与批量导入模板 / 飞书《SKU归一更新清单》对齐:
// SKU编号 / 产品中文名称 / 产品英文名称 / 材质编码 / 商品链接, 另加属性 / 状态 / CMS 耗材ID / 规格 (原材料必填).
// 材质编码可空 = 暂不绑定材料, 后续可在批量导入里补绑.
function SkuCreateModal({ open, onClose, onCreated, presetXcode }: Props) {
    const [skuCode, setSkuCode] = useState('');
    const [nameZh, setNameZh] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [xcode, setXcode] = useState('');
    const [link, setLink] = useState('');
    const [prop, setProp] = useState('');
    const [status, setStatus] = useState('on_shelf');
    const [cmsId, setCmsId] = useState('');
    const [spec, setSpec] = useState(EMPTY_SKU_SPEC);
    const { byKey: specSchemas } = useSkuSchemas();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setSkuCode('');
        setNameZh('');
        setNameEn('');
        setXcode(presetXcode || '');
        setLink('');
        setProp('');
        setStatus('on_shelf');
        setCmsId('');
        setSpec(EMPTY_SKU_SPEC);
        setError('');
    }, [open, presetXcode]);

    const onSubmit = async () => {
        const code = skuCode.trim();
        if (!code) {
            setError('SKU 编号必填');
            return;
        }
        if (cmsId.trim() && !/^\d+$/.test(cmsId.trim())) {
            setError('CMS 耗材ID 必须是整数');
            return;
        }
        const [specPayload, specErr] = validateSkuSpec(prop, spec, specSchemas);
        if (specErr) {
            setError(specErr);
            return;
        }
        const productName: I18n = {};
        if (nameZh.trim()) productName.zh = nameZh.trim();
        if (nameEn.trim()) productName.en = nameEn.trim();
        const payload: CreateSkuPayload & { xcode?: string } = { skuCode: code, status };
        if (xcode.trim()) payload.xcode = xcode.trim();
        if (Object.keys(productName).length > 0) payload.productName = productName;
        if (prop) payload.prop = prop;
        if (link.trim()) payload.remark = link.trim();
        if (cmsId.trim()) payload.cmsId = Number(cmsId.trim());
        if (specPayload.specGroup) Object.assign(payload, specPayload);

        setSubmitting(true);
        setError('');
        try {
            const res = await createSku(payload);
            if (alertApprovalIfPending(res.message)) {
                onClose();
                return;
            }
            toast(`SKU ${code} 已创建`);
            onCreated?.();
            onClose();
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="新增 SKU" width={680}>
            <div className="space-y-4">
                <div className="text-xs text-slate-500">
                    字段与批量导入模板一致; 材质编码可留空 (暂不绑定材料), 之后可在材料详情或批量导入里补绑.
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>SKU 编号
                        </label>
                        <input
                            className="inp font-mono"
                            value={skuCode}
                            onChange={(e) => setSkuCode(e.target.value)}
                            placeholder="例: P0104001274"
                        />
                    </div>
                    <div>
                        <label className="field-label">材质编码 (可空)</label>
                        <SearchSelect
                            loadOptions={loadMaterialOptions}
                            value={xcode}
                            valueLabel={xcode}
                            onChange={(v) => setXcode(v)}
                            placeholder="搜索材质编码 / 材料名"
                            className="w-full"
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">产品中文名称</label>
                        <input
                            className="inp"
                            value={nameZh}
                            onChange={(e) => setNameZh(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="field-label">产品英文名称</label>
                        <input
                            className="inp"
                            value={nameEn}
                            onChange={(e) => setNameEn(e.target.value)}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">商品链接 (存入备注)</label>
                        <input
                            className="inp"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            placeholder="https://…"
                        />
                    </div>
                    <div>
                        <label className="field-label">属性</label>
                        <SkuPropSelect className="sel w-full" value={prop} onChange={setProp} />
                    </div>
                    <div>
                        <label className="field-label">状态</label>
                        <SearchSelect
                            className="w-full"
                            value={status}
                            onChange={(v) => setStatus(v)}
                            options={SKU_STATUS_OPTS}
                        />
                    </div>
                    <div>
                        <label className="field-label">CMS 耗材ID (可空)</label>
                        <input
                            className="inp font-mono"
                            value={cmsId}
                            onChange={(e) => setCmsId(e.target.value)}
                            placeholder="仅数字"
                        />
                    </div>
                    <div className="col-span-2">
                        <SkuSpecInput prop={prop} value={spec} onChange={setSpec} />
                    </div>
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-ghost"
                        disabled={submitting}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onSubmit}
                        disabled={submitting}
                    >
                        {submitting ? '提交中…' : '确定'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default SkuCreateModal;
